import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const priorityColors = {
  low: 'bg-green-500/20 text-green-400 border-green-500/50',
  medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50',
  high: 'bg-orange-500/20 text-orange-400 border-orange-500/50',
  urgent: 'bg-red-500/20 text-red-400 border-red-500/50',
  very_urgent: 'bg-pink-500/20 text-pink-400 border-pink-500/50'
};

const priorityLabels = {
  low: 'Düşük',
  medium: 'Orta',
  high: 'Yüksek',
  urgent: 'Acil',
  very_urgent: 'Çok Acil'
};

const statusColors = {
  not_started: 'bg-gray-500/20 text-gray-400 border-gray-500/50',
  planned: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/50',
  in_progress: 'bg-blue-500/20 text-blue-400 border-blue-500/50',
  project_work: 'bg-purple-500/20 text-purple-400 border-purple-500/50',
  tender_phase: 'bg-orange-500/20 text-orange-400 border-orange-500/50',
  completed: 'bg-green-500/20 text-green-400 border-green-500/50'
};

const statusLabels = {
  not_started: 'Başlanmadı',
  planned: 'Planlanan',
  in_progress: 'Devam Ediyor',
  project_work: 'Proje İşleri',
  tender_phase: 'İhale Aşamasında',
  completed: 'Tamamlandı'
};

const ArchivePage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [archivedTasks, setArchivedTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchArchivedTasks();
  }, []);

  const fetchArchivedTasks = async () => {
    try {
      const response = await axios.get(`${API}/tasks?archived=true`, {
        withCredentials: true
      });
      setArchivedTasks(response.data);
    } catch (error) {
      console.error('Archived tasks fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    if (!window.confirm(`Durumu "${statusLabels[newStatus]}" olarak değiştirmek istediğinizden emin misiniz?${newStatus !== 'completed' ? '\n\nBu görev arşivden çıkıp aktif görevlere taşınacak.' : ''}`)) {
      return;
    }
    
    try {
      await axios.patch(
        `${API}/tasks/${taskId}/status`,
        { status: newStatus },
        { withCredentials: true }
      );
      
      // Show success message
      alert('✅ Durum başarıyla güncellendi!');
      
      // If status changed to non-completed, task will be unarchived
      if (newStatus !== 'completed') {
        // Redirect to tasks page
        navigate('/tasks');
      } else {
        // Refresh archived tasks
        await fetchArchivedTasks();
      }
    } catch (error) {
      console.error('Status update error:', error);
      alert('❌ Durum güncellenirken hata oluştu: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-white">Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950">
      {/* Header */}
      <header className="bg-gray-900/50 border-b border-gray-800 backdrop-blur-sm sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img src="/logo.png" alt="Kurumsal İş Yönetimi, Takip ve Koordinasyon Platformu Logo" className="w-12 h-12 object-contain cursor-pointer" onClick={() => navigate('/dashboard')} />
              <div>
                <h1 className="text-xl font-bold text-white">Kurumsal İş Yönetimi, Takip ve Koordinasyon Platformu</h1>
                <p className="text-xs text-gray-400">Yatırım ve İnşaat</p>
              </div>
            </div>
            <div className="flex items-center space-x-1">
              <button
                onClick={() => navigate('/')}
                className="px-4 py-2 text-gray-300 hover:text-white hover:bg-gray-800/50 rounded-lg transition-colors"
                data-testid="anasayfa-nav-button"
              >
                Anasayfa
              </button>
              <span className="text-gray-600">|</span>
              <button
                onClick={() => navigate('/dashboard')}
                className="px-4 py-2 text-gray-300 hover:text-white hover:bg-gray-800/50 rounded-lg transition-colors"
              >
                Dashboard
              </button>
              <span className="text-gray-600">|</span>
              <button
                onClick={() => navigate('/tasks')}
                className="px-4 py-2 text-gray-300 hover:text-white hover:bg-gray-800/50 rounded-lg transition-colors"
              >
                Görevler
              </button>
              <span className="text-gray-600">|</span>
              <button
                onClick={() => navigate('/calendar')}
                className="px-4 py-2 text-gray-300 hover:text-white hover:bg-gray-800/50 rounded-lg transition-colors"
              >
                Takvim
              </button>
              <span className="text-gray-600">|</span>
              <button
                onClick={() => navigate('/archive')}
                className="px-4 py-2 text-gray-300 hover:text-white hover:bg-gray-800/50 rounded-lg transition-colors border-b-2 border-blue-500"
              >
                Arşiv
              </button>
              {(user?.is_admin || user?.is_admin_assistant) && (
                <>
                  <span className="text-gray-600">|</span>
                  <button
                    onClick={() => navigate('/admin/users')}
                    className="px-4 py-2 text-gray-300 hover:text-white hover:bg-gray-800/50 rounded-lg transition-colors"
                  >
                    Kullanıcılar
                  </button>
                </>
              )}
              <div className="ml-4 border-l border-gray-700 pl-4 flex items-center space-x-4">
                <div 
                  className="flex items-center space-x-3 bg-gray-800/50 rounded-lg px-4 py-2 cursor-pointer hover:bg-gray-800/70 transition-colors"
                  onClick={() => navigate('/profile')}
                >
                  <img src={user?.picture} alt={user?.name} className="w-8 h-8 rounded-full" />
                  <div className="text-left">
                    <p className="text-sm font-medium text-white">{user?.name}</p>
                    <p className="text-xs text-gray-400">
                      {user?.is_admin ? 'Admin' : user?.is_admin_assistant ? 'Admin Yardımcısı' : 'Kullanıcı'}
                    </p>
                  </div>
                </div>
                <Button
                  onClick={handleLogout}
                  variant="ghost"
                  className="text-red-400 hover:text-red-300"
                >
                  Çıkış
                </Button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-white mb-2">📦 Arşiv - Tamamlanan Görevler</h2>
          <p className="text-gray-400">
            {user?.is_admin 
              ? 'Tüm tamamlanan görevler' 
              : user?.is_admin_assistant 
                ? 'Alt biriminizin tamamlanan görevleri'
                : 'Tamamladığınız görevler'}
          </p>
        </div>

        {/* Archived Tasks Grid */}
        {archivedTasks.length === 0 ? (
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="py-12 text-center">
              <p className="text-gray-400">Henüz arşivlenmiş görev yok</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {archivedTasks.map((task) => (
              <Card
                key={task.id}
                className="bg-gray-900 border-green-800/30"
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-white text-lg">{task.title}</CardTitle>
                    <Badge className="bg-green-500/20 text-green-400 border-green-500/50">
                      Tamamlandı
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-400 mt-2 line-clamp-2">{task.description}</p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">Öncelik:</span>
                      <Badge className={priorityColors[task.priority]} variant="outline">
                        {priorityLabels[task.priority]}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-500">Tamamlanma:</span>
                      <span className="text-green-400">
                        {task.completed_at && new Date(task.completed_at).toLocaleDateString('tr-TR')}
                      </span>
                    </div>
                  </div>
                  <div className="pt-2 border-t border-gray-800">
                    <p className="text-xs text-gray-500 mb-1">Atanan:</p>
                    <p className="text-sm text-gray-300">{task.assigned_to_email || '-'}</p>
                    {task.assigned_to_2_email && (
                      <p className="text-sm text-gray-300 mt-1">{task.assigned_to_2_email}</p>
                    )}
                    {task.assigned_to_3_email && (
                      <p className="text-sm text-gray-300 mt-1">{task.assigned_to_3_email}</p>
                    )}
                  </div>

                  {/* Status Change (Admin & Admin Assistant) */}
                  {(user?.is_admin || user?.is_admin_assistant) && (
                    <div className="pt-2 border-t border-gray-800">
                      <Label className="text-xs text-gray-500 mb-2 block">Durum Güncelle:</Label>
                      <Select
                        value={task.status}
                        onValueChange={(value) => handleStatusChange(task.id, value)}
                      >
                        <SelectTrigger className="bg-gray-800 border-gray-700 text-white text-xs h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-gray-900 border-gray-800 text-white">
                          <SelectItem value="not_started">Başlanmadı</SelectItem>
                          <SelectItem value="planned">Planlanan</SelectItem>
                          <SelectItem value="in_progress">Devam Ediyor</SelectItem>
                          <SelectItem value="project_work">Proje İşleri</SelectItem>
                          <SelectItem value="tender_phase">İhale Aşamasında</SelectItem>
                          <SelectItem value="completed">Tamamlandı</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default ArchivePage;
